// src/hooks/useReports.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReportService, ReportStats } from '../admin/pages/report/reportService';
import { ReportType, ReportFilters } from '../admin/pages/report/Report';

export const REPORT_KEYS = {
  all:       ['reports'] as const,
  lists:     () => ['reports', 'list'] as const,
  paginated: (page: number, search: string, type: string, status: string) =>
               ['reports', 'list', page, search, type, status] as const,
  stats:     () => ['reports', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface ReportPaginationParams {
  page: number;
  search: string;
  typeFilter: string;
  statusFilter: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getReportsPaginated instead of cursor
// objects. Any page can now be fetched in any order.
export const useReportsPaginated = (params: ReportPaginationParams) => {
  return useQuery({
    queryKey: REPORT_KEYS.paginated(params.page, params.search, params.typeFilter, params.statusFilter),
    queryFn: () =>
      ReportService.getReportsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.typeFilter   !== 'all' ? (params.typeFilter   as ReportType)                              : undefined,
        params.statusFilter !== 'all' ? (params.statusFilter as 'completed' | 'generating' | 'failed')  : undefined,
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

export const useReportStats = () => {
  return useQuery({
    queryKey: REPORT_KEYS.stats(),
    queryFn: () => ReportService.getReportStats(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useReportMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: REPORT_KEYS.all });
  };

  const generate = useMutation({
    mutationFn: ({
      reportType, reportName, description, filters, userId, userName,
    }: {
      reportType: ReportType;
      reportName: string;
      description: string;
      filters: ReportFilters;
      userId: string;
      userName: string;
    }) => ReportService.generateReport(reportType, reportName, description, filters, userId, userName),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (reportId: string) => ReportService.deleteReport(reportId),
    onSuccess: invalidateAll,
  });

  return { generate, remove };
};